// White-label / Agency mode branding settings (audit-handoff Phase 3 §B.1).
//
// Owner-only panel for setting logo, colours, login subtitle, custom domain
// and the enterprise-gated "hide xpnsr branding" toggle. The form holds a
// local copy of the settings; on save we PUT it and refresh the
// `BrandingProvider` so the rest of the app sees the new palette.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import { Palette, Save, RotateCcw, Eye } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { useBranding } from '../branding';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import XpnsrPageHeader from './XpnsrPageHeader';

const FIELDS = [
  'logo_url',
  'primary_color',
  'secondary_color',
  'login_subtitle',
  'custom_domain',
  'hide_xpnsr_branding',
];

function blank() {
  return {
    logo_url: '',
    primary_color: '',
    secondary_color: '',
    login_subtitle: '',
    custom_domain: '',
    hide_xpnsr_branding: false,
  };
}

function fromServer(doc) {
  const out = blank();
  if (!doc) return out;
  for (const key of FIELDS) {
    if (key === 'hide_xpnsr_branding') {
      out[key] = !!doc[key];
    } else if (doc[key] !== undefined && doc[key] !== null) {
      out[key] = doc[key];
    }
  }
  return out;
}

export default function Branding() {
  const { t } = useT();
  const { refresh: refreshBranding } = useBranding();
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/workspace/branding`);
      setForm(fromServer(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Could not load branding'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Send empty strings as `null` so the backend understands "clear this".
      const payload = {};
      for (const key of FIELDS) {
        if (key === 'hide_xpnsr_branding') {
          payload[key] = !!form[key];
          continue;
        }
        const value = (form[key] ?? '').trim();
        payload[key] = value === '' ? null : value;
      }
      const r = await axios.put(`${API}/workspace/branding`, payload);
      setForm(fromServer(r.data));
      await refreshBranding();
      toast.success(t('Branding saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Could not save branding'));
    } finally {
      setSaving(false);
    }
  };

  const [resetOpen, setResetOpen] = useState(false);

  const reset = async () => {
    setResetOpen(false);
    setResetting(true);
    try {
      await axios.delete(`${API}/workspace/branding`);
      setForm(blank());
      await refreshBranding();
      toast.success(t('Branding cleared'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Could not reset branding'));
    } finally {
      setResetting(false);
    }
  };

  const previewStyle = {
    backgroundColor: form.primary_color || '#0ea5e9',
    color: '#fff',
  };
  const previewSecondary = {
    backgroundColor: form.secondary_color || '#1f2937',
    color: '#fff',
  };

  return (
    <div className="xp-page">
      <XpnsrPageHeader
        icon={Palette}
        title={t('Branding')}
        subtitle={t('Customise the dashboard for your agency or sub-account')}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('Visual identity')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="logo_url">{t('Logo URL')}</Label>
              <Input
                id="logo_url"
                placeholder="https://cdn.acme.com/logo.png"
                value={form.logo_url}
                onChange={set('logo_url')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('Must be an https:// URL. Leave blank to fall back to the xpnsr logo.')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="primary_color">{t('Primary colour')}</Label>
                <Input
                  id="primary_color"
                  placeholder="#0ea5e9"
                  value={form.primary_color}
                  onChange={set('primary_color')}
                />
              </div>
              <div>
                <Label htmlFor="secondary_color">{t('Secondary colour')}</Label>
                <Input
                  id="secondary_color"
                  placeholder="#1f2937"
                  value={form.secondary_color}
                  onChange={set('secondary_color')}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="login_subtitle">{t('Login page subtitle')}</Label>
              <Textarea
                id="login_subtitle"
                rows={3}
                maxLength={280}
                placeholder={t('Welcome to ACME Tracker — sign in to continue')}
                value={form.login_subtitle}
                onChange={set('login_subtitle')}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('Custom domain')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="custom_domain">{t('Dashboard hostname')}</Label>
              <Input
                id="custom_domain"
                placeholder="tracker.acme.com"
                value={form.custom_domain}
                onChange={set('custom_domain')}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('Point this hostname at the tracker via DNS. Visitors hitting it will see your branding before they sign in.')}
              </p>
            </div>
            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label className="font-medium">{t('Hide xpnsr branding')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('Available on enterprise plans. Hides the "Powered by xpnsr" footer.')}
                </p>
              </div>
              <Switch
                checked={form.hide_xpnsr_branding}
                onCheckedChange={(v) => set('hide_xpnsr_branding')(v)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="w-4 h-4" /> {t('Preview')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div
              className="rounded-lg flex items-center justify-center w-32 h-32 overflow-hidden"
              style={previewStyle}
            >
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt=""
                  className="object-contain max-w-full max-h-full"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span className="text-xs uppercase tracking-widest opacity-80">{t('logo')}</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="px-3 py-1.5 rounded text-xs font-mono" style={previewStyle}>
                {form.primary_color || '#0ea5e9'}
              </div>
              <div className="px-3 py-1.5 rounded text-xs font-mono" style={previewSecondary}>
                {form.secondary_color || '#1f2937'}
              </div>
              {form.login_subtitle ? (
                <div className="text-sm text-muted-foreground italic max-w-xs">
                  &ldquo;{form.login_subtitle}&rdquo;
                </div>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 mt-6">
        <Button onClick={save} disabled={saving || loading}>
          <Save className="w-4 h-4 mr-2" /> {saving ? t('Saving…') : t('Save branding')}
        </Button>
        <Button variant="outline" onClick={() => setResetOpen(true)} disabled={resetting || loading}>
          <RotateCcw className="w-4 h-4 mr-2" /> {resetting ? t('Resetting…') : t('Reset to defaults')}
        </Button>
      </div>

      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={t('Reset branding')}
        description={t('This will reset all branding settings to defaults. Logo, colors, and custom domain will be removed.')}
        confirmLabel={t('Reset')}
        onConfirm={reset}
      />
    </div>
  );
}
